import {
    all,
    call,
    put,
    select,
    takeLatest,
    take,
    takeEvery,
    fork,
    cancel,
    cancelled,
} from 'redux-saga/effects';
import * as Sentry from '@sentry/react';
import {
    userSegmentsSucceeded,
    userSegmentsFailed,
    segmentTagsSucceeded,
    segmentTagsFailed,
    segmentPhotosSucceeded,
    segmentPhotosFailed,
    updateSegmentNotesSucceeded,
    updateSegmentNotesFailed,
    updateSegmentNameSucceeded,
    updateSegmentNameFailed,
    deleteSegmentSucceeded,
    deleteSegmentFailed,
    partnersSucceeded,
    partnersFailed,
} from './actions';
import { getUser } from '../auth/selectors';
import {
    getDate,
    getCurrentPage,
    getLandtype,
    getSubLandtype,
    getSelectedPartners,
} from './selectors';
import { parseClientError } from '../../api/utils';
import {
    PARTNERS_REQUESTED,
    USER_SEGMENTS_REQUESTED,
    SEGMENT_TAGS_REQUESTED,
    MORE_USER_SEGMENTS_REQUESTED,
    SEGMENT_PHOTOS_REQUESTED,
    UPDATE_SEGMENT_NOTES_REQUESTED,
    UPDATE_SEGMENT_NAME_REQUESTED,
    DELETE_SEGMENT_REQUESTED,
} from './constants';
import {
    getUserSegments,
    getSegmentTags,
    getMLTags,
    getSegmentImages,
    updateSegment,
    deleteSegment,
    getSegmentLitters,
    updateSegmentName,
    getCScientist,
} from '../../api/index';
import { LANDUSED_TYPE } from '../../common/constants';
import { PICTURE_LABELS } from './SegmentPhotos/constants';
import { arrayOfObjectsToIdsStr } from '../../common/utils';
import { getUserData } from '../auth/selectors';
import { logException } from '../../logger';

function* fetchUserSegmentsList() {
    const userData = yield select(getUserData);
    try {
        const { id } = yield select(getUser);
        const date = yield select(getDate);
        const skip = yield select(getCurrentPage);
        const landType = yield select(getLandtype);
        const subLandType = yield select(getSubLandtype);
        let selectedPartners = yield select(getSelectedPartners);
        if (
            selectedPartners &&
            selectedPartners.length &&
            selectedPartners[0].id !== 'ALL'
        ) {
            selectedPartners = arrayOfObjectsToIdsStr(selectedPartners);
        } else {
            selectedPartners = null;
        }
        const params = {
            userId: id,
            ...date,
            skip,
        };

        if (landType) {
            params.landType = landType;
            if (landType == LANDUSED_TYPE[1].key) {
                if (subLandType) params.subLandType = subLandType;
            }
        }
        if (selectedPartners) {
            params.userIds = selectedPartners;
        }

        const segments = yield call(getUserSegments, params);
        yield put(userSegmentsSucceeded(segments));
    } catch (e) {
        Sentry.captureException(e);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(userSegmentsFailed(message));
    }
}

function* fetchTags(action) {
    const userData = yield select(getUserData);
    const { segmentId } = action;
    const { segmentTaxonomy } = action;

    try {
        let params = {};
        params = {
            segmentId,
            params: { activityId: segmentId, taxonomy: segmentTaxonomy },
        };
        let result = {};
        const [userTags, mlTags] = yield all([
            yield call(getSegmentTags, params),
            yield call(getMLTags, {
                taxonomy: segmentTaxonomy,
                activityId: segmentId,
            }),
        ]);
        // combine result to update in state
        result[segmentId] = {
            // dynamically set object key in order to select appropriate segment tags
            userTags: userTags.tags ? userTags.tags : [],
            mlTags: mlTags.tags ? mlTags.tags : [],
        };
        yield put(segmentTagsSucceeded(result));
    } catch (e) {
        Sentry.captureException(e);
        logException(e, { ...userData, segmentId, segmentTaxonomy });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(segmentTagsFailed(message));
    } finally {
        if (yield cancelled()) {
            yield put(segmentTagsFailed('sync cancelled'));
        }
    }
}

function* fetchSegmentTags(action) {
    try {
        // fetch tasks in background
        const bgTask = yield fork(fetchTags, action);
        // wait for the user stop action
        yield take(USER_SEGMENTS_REQUESTED);
        yield cancel(bgTask);
    } catch (e) {
        Sentry.captureException(e);
        logException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(segmentTagsFailed(message));
    }
}

function* fetchPhotos(action) {
    const userData = yield select(getUserData);
    try {
        let params = {};
        const { segmentId, userId } = action;
        params = {
            activityId: segmentId,
            userId,
            isValid: true,
        };
        let result = {};
        const images = yield call(getSegmentImages, params);
        let litters = yield call(getSegmentLitters, params);
        litters = litters
            ? litters.map((litter) => ({
                  ...litter,
                  classification: PICTURE_LABELS.LITTER,
              }))
            : [];
        result[segmentId] = [...images, ...litters] || [];

        yield put(segmentPhotosSucceeded(result));
    } catch (e) {
        Sentry.captureException(e);
        logException(e, { ...userData, segmentId: action.segmentId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(segmentPhotosFailed(message));
    } finally {
        if (yield cancelled()) {
            yield put(segmentPhotosFailed('sync cancelled'));
        }
    }
}

function* fetchSegmentPhotos(action) {
    try {
        // fetch tasks in background
        const bgTask = yield fork(fetchPhotos, action);
        // wait for the user stop action
        yield take(USER_SEGMENTS_REQUESTED);
        yield cancel(bgTask);
    } catch (e) {
        Sentry.captureException(e);
        logException(e);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(segmentTagsFailed(message));
    }
}

function* updateSegmentNotes(action) {
    try {
        const { segmentId, notes } = action;

        const segment = yield call(updateSegment, { segmentId, notes });
        yield put(updateSegmentNotesSucceeded(segment));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(updateSegmentNotesFailed(message));
    }
}

function* addSegmentName(action) {
    try {
        const { segmentId, name } = action;

        const segment = yield call(updateSegmentName, { segmentId, name });
        yield put(updateSegmentNameSucceeded({ ...segment, name }));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(updateSegmentNameFailed(message));
    }
}

function* deleteSegmentById(action) {
    try {
        const { segmentId } = action;

        yield call(deleteSegment, { segmentId });
        yield put(deleteSegmentSucceeded(segmentId));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData, segmentId: action.segmentId });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(deleteSegmentFailed(message));
    }
}

function* getPartners() {
    try {
        //const partnerId = yield select(getPartnerId);
        let partners = yield call(getCScientist);
        partners = partners.data.users || [];
        yield put(partnersSucceeded(partners));
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, { ...userData });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(partnersFailed(message));
    }
}

export default function*() {
    yield all([
        yield takeLatest(USER_SEGMENTS_REQUESTED, fetchUserSegmentsList),
        yield takeLatest(MORE_USER_SEGMENTS_REQUESTED, fetchUserSegmentsList),
        yield takeEvery(SEGMENT_TAGS_REQUESTED, fetchSegmentTags),
        yield takeEvery(SEGMENT_PHOTOS_REQUESTED, fetchSegmentPhotos),
        yield takeEvery(UPDATE_SEGMENT_NOTES_REQUESTED, updateSegmentNotes),
        yield takeEvery(UPDATE_SEGMENT_NAME_REQUESTED, addSegmentName),
        yield takeEvery(DELETE_SEGMENT_REQUESTED, deleteSegmentById),
        yield takeEvery(PARTNERS_REQUESTED, getPartners),
    ]);
}
